import { z } from 'zod';
import {
  DescuentoParceladoDetailSchema,
  DescuentoParceladoRequestSchema,
  DescuentoParceladoViewSchema,
  MultaListQuerySchema,
  MultaRequestSchema,
  MultaViewSchema,
  PlanillaGeneralViewSchema,
  PlanillaViewSchema,
} from '@visep/shared-types/planilla';
import type {
  DescuentoParceladoDetail,
  DescuentoParceladoRequest,
  DescuentoParceladoView,
  MultaListQuery,
  MultaRequest,
  MultaView,
  PlanillaGeneralView,
  PlanillaView,
} from '@visep/shared-types/planilla';
import type { Helpers } from './internal.js';
import { toQuery } from './internal.js';

const ReprogramarResultSchema = z.object({
  id: z.string(),
  periodoAnterior: z.string(),
  periodoNuevo: z.string(),
});
export type ReprogramarResult = z.infer<typeof ReprogramarResultSchema>;

export function createPlanillaResource({ request, requestBlob }: Helpers) {
  return {
    async getPlanilla(periodo: string, sucursalId: string): Promise<PlanillaView> {
      return PlanillaViewSchema.parse(await request('GET', `/api/planilla${toQuery({ periodo, sucursalId })}`));
    },
    async getGeneral(periodo: string): Promise<PlanillaGeneralView> {
      return PlanillaGeneralViewSchema.parse(await request('GET', `/api/planilla/general${toQuery({ periodo })}`));
    },
    async cierre(periodo: string, sucursalId: string): Promise<PlanillaView> {
      return PlanillaViewSchema.parse(await request('POST', '/api/planilla/cierre', { periodo, sucursalId }));
    },
    async getPdf(periodo: string, sucursalId: string): Promise<Blob> {
      return requestBlob(`/api/planilla/pdf${toQuery({ periodo, sucursalId })}`);
    },
    async createDescuentoParcelado(input: DescuentoParceladoRequest): Promise<DescuentoParceladoView> {
      const parsed = DescuentoParceladoRequestSchema.parse(input);
      return DescuentoParceladoViewSchema.parse(await request('POST', '/api/descuentos-parcelados', parsed));
    },
    async listDescuentosParcelados(guardiaId: string): Promise<DescuentoParceladoDetail[]> {
      return z
        .array(DescuentoParceladoDetailSchema)
        .parse(await request('GET', `/api/descuentos-parcelados${toQuery({ guardiaId })}`));
    },
    async deleteDescuentoParcelado(id: string): Promise<void> {
      await request('DELETE', `/api/descuentos-parcelados/${encodeURIComponent(id)}`);
    },
    async reprogramarCuota(id: string): Promise<ReprogramarResult> {
      return ReprogramarResultSchema.parse(
        await request('PATCH', `/api/descuentos-cuotas/${encodeURIComponent(id)}/reprogramar`, {}),
      );
    },
    async listMultas(query: MultaListQuery): Promise<MultaView[]> {
      const parsed = MultaListQuerySchema.parse(query);
      return z.array(MultaViewSchema).parse(await request('GET', `/api/multas${toQuery(parsed)}`));
    },
    async createMulta(input: MultaRequest): Promise<MultaView> {
      const parsed = MultaRequestSchema.parse(input);
      return MultaViewSchema.parse(await request('POST', '/api/multas', parsed));
    },
    async deleteMulta(id: string): Promise<void> {
      await request('DELETE', `/api/multas/${encodeURIComponent(id)}`);
    },
  };
}
